"use client";

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ImagePlus, Pencil, ScanBarcode } from "lucide-react";
import type { ProductRow } from "@/lib/types/product";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiBaseURL } from "@/lib/api-config";
import { cn } from "@/lib/utils";

type Props = {
  data: ProductRow[];
  rowSelection: RowSelectionState;
  onRowSelectionChange: (s: RowSelectionState) => void;
  onEdit: (p: ProductRow) => void;
  onBarcode: (p: ProductRow) => void;
  onImage: (p: ProductRow) => void;
  onDelete: (p: ProductRow) => void;
};

function imgSrc(url: string | null | undefined) {
  if (!url) {
    return "";
  }
  if (url.startsWith("http")) {
    return url;
  }

  return `${apiBaseURL}${url}`;
}

export function ProductTable({
  data,
  rowSelection,
  onRowSelectionChange,
  onEdit,
  onBarcode,
  onImage,
  onDelete,
}: Props) {
  const columns = useMemo<ColumnDef<ProductRow>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label="Select all"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label="Select row"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 36,
      },
      {
        id: "image",
        header: "Image",
        cell: ({ row }) => {
          const src = imgSrc(row.original.image_url);
          return (
            <button
              type="button"
              title={src ? "View image" : "Add image"}
              onClick={() => onImage(row.original)}
              className="group relative h-10 w-10 overflow-hidden rounded-md border bg-muted transition hover:ring-2 hover:ring-sky-300"
            >
              {src ? (
                <img
                  src={src}
                  alt=""
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <ImagePlus className="h-4 w-4" />
                </span>
              )}
            </button>
          );
        },
      },
      { accessorKey: "name", header: "Name" },
      { accessorKey: "sku", header: "SKU" },
      {
        accessorKey: "category_name",
        header: "Category",
        cell: ({ row }) => row.original.category_name ?? "—",
      },
      {
        accessorKey: "stock_qty",
        header: "Stock",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {Number(row.original.stock_qty).toFixed(3)}
          </span>
        ),
      },
      {
        accessorKey: "selling_price",
        header: "Price",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            ₹{Number(row.original.selling_price).toFixed(2)}
          </span>
        ),
      },
      {
        accessorKey: "gst_rate",
        header: "GST",
        cell: ({ row }) => `${Number(row.original.gst_rate).toFixed(2)}%`,
      },
      {
        accessorKey: "is_active",
        header: "Status",
        cell: ({ row }) =>
          row.original.is_active ? (
            <Badge variant="secondary">Active</Badge>
          ) : (
            <Badge variant="outline">Inactive</Badge>
          ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Link
              to={`/products/${row.original.id}`}
              title="Detail"
              className={cn(buttonVariants({ variant: "ghost", size: "icon" }))}
            >
              <span className="sr-only">Detail</span>
              <span className="text-xs font-medium">#</span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              title="Edit"
              onClick={() => onEdit(row.original)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Barcode"
              onClick={() => onBarcode(row.original)}
            >
              <ScanBarcode className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
      {
        id: "delete",
        header: "",
        cell: ({ row }) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete(row.original)}
          >
            Delete
          </Button>
        ),
      },
    ],
    [onBarcode, onDelete, onEdit, onImage],
  );

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection },
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      onRowSelectionChange(next);
    },
    getRowId: (row) => String(row.id),
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[960px] text-sm">
        <thead className="bg-muted/50">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className={cn(
                    "border-b px-3 py-2 text-left font-medium text-muted-foreground",
                    h.column.id === "select" && "w-10",
                  )}
                >
                  {h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="border-b last:border-0 hover:bg-muted/30">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2 align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
